import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from "react";
import { Redirect, useHistory, useLocation } from "react-router";
import Select from "react-select";
import HttpService from "../../services/HttpService";
import RenderOnRole from "../../Utilities/RenderOnRole";
import FilterManager from "./Components/ErrorFilters";
import FlaggedState from "./Components/FlaggedState";
import "../../css/index.css";
import "../../css/Pages.css";
import { useTranslation } from 'react-i18next';

const initialFormState = {
  redirect: false,
  interface: null,
  interfaceId: null,
  aanmaakdatum: null,
  klant: null,
  abonnement: null,
  interfaceArtikel: null,
  postzegel: null,
  startdatum: null,
  einddatum: null,
  status: "Inactief",
  statusOverschreven: false,
  mkgVerzender: null,
  mkgOntvanger: null,
  wachtwoord: null,
  auth: null,
  sftpHost: null,
  sftpPort: null,
  sftpMap: null,
  sftpUsername: null,
  sftpPassword: null,
  gebruiktNokavision: false,
  PDFMap: null,
  connectionType: null,
  GLNCode: null,
  Note: null,
  Creadentials: null,
  mail: null,
  DynamicListener: null,
  refreshToken: null,
  accessToken: null,
  error: false,
  aantal: 1,
  factuur_referentie: null,
  cp_interface: null
};

const NO_RECEIVER_AUTH = "__NO_RECEIVER__";

const mergeReducer = (state, updates) => ({ ...state, ...updates });

const parseCsvValues = (value) =>
  (value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const toCsvValues = (items) =>
  items
    .map((item) => (item || "").trim())
    .filter(Boolean)
    .join(",");

const computeBeschikbareArtikelen = (nieuwAbonnement, artikelen = [], interfaceArtikel) => {
  let beschikbare = [];
  let usePostzegel = true;
  let selectedArtikel = interfaceArtikel || null;

  if (nieuwAbonnement) {
    const abonnementType = nieuwAbonnement.abonnementType || {};

    if (abonnementType.verkoopgroep_relanumartikel != null) {
      const verkoopgroep = abonnementType.verkoopgroep_relanumartikel;
      const matches = artikelen.filter((art) => art.artikelZoekCode === verkoopgroep);
      beschikbare = beschikbare.concat(matches);
      if (interfaceArtikel && interfaceArtikel.artikelZoekCode === verkoopgroep) {
        selectedArtikel = interfaceArtikel;
      }
    }

    if (abonnementType.mkg_artikelzoekcode === "arti8010") {
      const matches = artikelen.filter((art) => art.artikelZoekCode === "8020");
      beschikbare = beschikbare.concat(matches);
      if (interfaceArtikel && interfaceArtikel.artikelZoekCode === "8020") {
        selectedArtikel = interfaceArtikel;
      }
    }

    if (abonnementType.mkg_artikelzoekcode === "arti8030") {
      const matches = artikelen.filter((art) => art.artikelZoekCode === "8030");
      beschikbare = beschikbare.concat(matches);
      if (interfaceArtikel && interfaceArtikel.artikelZoekCode === "8030") {
        selectedArtikel = interfaceArtikel;
      }
      usePostzegel = false;
    }
  } else {
    beschikbare = artikelen;
  }

  if (!selectedArtikel && beschikbare.length > 0) {
    selectedArtikel = beschikbare[0];
  }

  return {
    beschikbareArtikelen: beschikbare,
    selectedArtikel,
    usePostzegel
  };
};

const InterfaceToevoegenPagina = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const locationState = location.state || {};

  const [formState, setFormState] = useReducer(mergeReducer, initialFormState);
  const [abonnementen, setAbonnementen] = useState([]);
  const [artikelen, setArtikelen] = useState(null);
  const [postzegels, setPostzegels] = useState(null);
  const [relaties, setRelaties] = useState(null);
  const [beschikbareArtikelen, setBeschikbareArtikelen] = useState([]);
  const [usePostzegel, setUsePostzegel] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const [responseIsSuccess, setResponseIsSuccess] = useState(false);
  const [messageKey, setMessageKey] = useState(0);
  const [redirect, setRedirect] = useState(false);
  const [mailEntries, setMailEntries] = useState([""]);
  const [updateMode, setUpdateMode] = useState(Boolean(locationState?.selectedInterface));
  const [selectedInterface, setSelectedInterface] = useState(locationState?.selectedInterface || null);
  const [axiosClient, setAxiosClient] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({
    interface: true,
    auth: false,
    sftp: false,
    overig: true,
    melding: true,
    flagged: true
  });

  const filterManagerRef = useRef(null);
  const valuesAppliedRef = useRef(false);
  const axiosClientRef = useRef(null);

  const setFlashMessage = useCallback((message, isSuccess = false) => {
    setResponseMessage("");
    setResponseIsSuccess(false);
    setTimeout(() => {
      setResponseMessage(message);
      setResponseIsSuccess(isSuccess);
      setMessageKey(prev => prev + 1);
    }, 50);
  }, []);

  useEffect(() => {
    if (!locationState || !locationState.klantObject) {
      history.push("/");
      return;
    }

    setFormState({
      klant: locationState.klantObject,
      abonnement: locationState.abonnement || locationState.klantObject.abonnementen?.[0] || null,
      aanmaakdatum: locationState.selectedInterface?.aanmaakdatum || null,
      interfaceId: locationState.selectedInterface?.interfaceId || null
    });
    setAbonnementen(locationState.klantObject.abonnementen || []);
    setSelectedInterface(locationState.selectedInterface || null);
    setUpdateMode(Boolean(locationState.selectedInterface));
  }, [history, locationState]);

  useEffect(() => {
    const httpService = HttpService;
    httpService.configure();
    const axiosClientInstance = httpService.getAxiosClient();
    axiosClientRef.current = axiosClientInstance;
    setAxiosClient(axiosClientInstance);
  }, []);

  useEffect(() => {
    if (!axiosClientRef.current || !formState.klant) return;
    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        const postzegelsUrl = `${process.env.REACT_APP_API_URL}/Interfaces?Postzegels=true`;
        const relatiesUrl = `${process.env.REACT_APP_API_URL}/Relaties`;
        const artikelenUrl = `${process.env.REACT_APP_API_URL}/Interfaces?Klant=0&Artikelen=true`;

        const [postzegelsRes, relatiesRes, artikelenRes] = await Promise.all([
          axiosClientRef.current.get(postzegelsUrl),
          axiosClientRef.current.get(relatiesUrl),
          axiosClientRef.current.get(artikelenUrl)
        ]);

        if (cancelled) return;

        setPostzegels(postzegelsRes.data || []);
        setRelaties(relatiesRes.data || []);
        setArtikelen(artikelenRes.data || []);
      } catch (err) {
        if (cancelled) return;
        setError(typeof err.response !== "undefined" ? err.response.data.message : err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [axiosClientRef, formState.klant]);

  useEffect(() => {
    if (!artikelen || !formState.abonnement) return;
    const { beschikbareArtikelen: available, selectedArtikel, usePostzegel: shouldUsePostzegel } =
      computeBeschikbareArtikelen(formState.abonnement, artikelen, formState.interfaceArtikel);

    setBeschikbareArtikelen(available);
    setUsePostzegel(shouldUsePostzegel);
    setFormState((prev) => ({
      interfaceArtikel: selectedArtikel || prev.interfaceArtikel || available[0] || null,
      postzegel: shouldUsePostzegel ? prev.postzegel ?? null : null
    }));
  }, [artikelen, formState.abonnement, formState.interfaceArtikel, postzegels]);

  const applySelectedInterface = useCallback(() => {
    if (!selectedInterface || !artikelen || !relaties) return;
    if (valuesAppliedRef.current) return;

    const wachtwoordDecoded = selectedInterface.wachtwoord
      ? selectedInterface.wachtwoord
      : selectedInterface.auth
      ? atob(selectedInterface.auth.split("Basic ")[1]).split(`${selectedInterface.mkg_relaNr_ontvangt}:`)[1]
      : null;

    const abonnementValue =
      formState.abonnement || locationState.abonnement || abonnementen[0] || null;
    const interfaceArtikelValue = artikelen.find(
      (art) => art.artikelNr === selectedInterface.artikelNr
    );

    setFormState({
      interfaceId: selectedInterface.interfaceId,
      interface: selectedInterface,
      aanmaakdatum: selectedInterface.aanmaakdatum,
      abonnement: abonnementValue,
      interfaceArtikel: interfaceArtikelValue,
      postzegel: selectedInterface.postzegel,
      startdatum: selectedInterface.startdatum,
      einddatum: selectedInterface.einddatum,
      status: selectedInterface.status,
      statusOverschreven: selectedInterface.status_overschreven,
      mkgVerzender: relaties.find((rela) => rela.rela_num === selectedInterface.mkg_relaNr_verzend) ||
        null,
      mkgOntvanger: relaties.find((rela) => rela.rela_num === selectedInterface.mkg_relaNr_ontvangt) ||
        null,
      wachtwoord: wachtwoordDecoded,
      auth: selectedInterface.auth,
      sftpHost: selectedInterface.sftp_host,
      sftpPort: selectedInterface.sftp_poort,
      sftpMap: selectedInterface.sftp_map,
      sftpUsername: selectedInterface.sftp_gebruiker,
      sftpPassword: selectedInterface.sftp_wachtwoord,
      gebruiktNokavision: selectedInterface.gebruiktNokavision,
      PDFMap: selectedInterface.extra01,
      connectionType: selectedInterface.extra02,
      GLNCode: selectedInterface.extra03,
      Note: selectedInterface.extra04,
      Creadentials: selectedInterface.extra05,
      mail: selectedInterface.mailadres,
      DynamicListener: selectedInterface.listener,
      refreshToken: selectedInterface.refreshToken,
      accessToken: selectedInterface.accessToken,
      factuur_referentie: selectedInterface.factuur_referentie,
      cp_interface: selectedInterface.cp_interface,
      aantal: 1,
      error: false
    });

    const { beschikbareArtikelen: available, selectedArtikel, usePostzegel: shouldUsePostzegel } =
      computeBeschikbareArtikelen(abonnementValue, artikelen, interfaceArtikelValue);
    setBeschikbareArtikelen(available);
    setUsePostzegel(shouldUsePostzegel);
    setFormState((prev) => ({ interfaceArtikel: selectedArtikel || prev.interfaceArtikel }));
    valuesAppliedRef.current = true;
  }, [abonnementen, artikelen, formState.abonnement, locationState.abonnement, relaties, selectedInterface]);

  useEffect(() => {
    applySelectedInterface();
  }, [applySelectedInterface]);

  const interfaceArtikelOptions = useMemo(
    () =>
      (beschikbareArtikelen || []).map((opt, i) => ({
        label: opt.artikelNr.toString(),
        value: i
      })),
    [beschikbareArtikelen]
  );

  const relatieOptions = useMemo(
    () =>
      (relaties || []).map((opt, i) => ({
        label: `${opt.rela_num} ${opt.rela_naam}`,
        value: i
      })),
    [relaties]
  );

  const selectedInterfaceArtikelOption = useMemo(() => {
    if (!formState.interfaceArtikel) return interfaceArtikelOptions[0];
    const index = (beschikbareArtikelen || []).findIndex(
      (art) => art.artikelNr === formState.interfaceArtikel.artikelNr
    );
    return index >= 0 ? interfaceArtikelOptions[index] : interfaceArtikelOptions[0];
  }, [beschikbareArtikelen, formState.interfaceArtikel, interfaceArtikelOptions]);

  const selectedVerzenderOption = useMemo(() => {
    if (!formState.mkgVerzender) return null;
    const index = (relaties || []).findIndex(
      (rela) => rela.rela_num === formState.mkgVerzender.rela_num
    );
    return index >= 0 ? relatieOptions[index] : null;
  }, [formState.mkgVerzender, relatieOptions, relaties]);

  const selectedOntvangerOption = useMemo(() => {
    if (!formState.mkgOntvanger) return null;
    const index = (relaties || []).findIndex(
      (rela) => rela.rela_num === formState.mkgOntvanger.rela_num
    );
    return index >= 0 ? relatieOptions[index] : null;
  }, [formState.mkgOntvanger, relatieOptions, relaties]);

  const postzegelIndex = useMemo(() => {
    if (!postzegels || !formState.postzegel) return "";
    const idx = postzegels.findIndex((post) => post.code === formState.postzegel.code);
    return idx >= 0 ? idx : "";
  }, [formState.postzegel, postzegels]);

  useEffect(() => {
    const parsedMails = parseCsvValues(formState.mail);
    setMailEntries(parsedMails.length > 0 ? parsedMails : [""]);
  }, [formState.mail]);

  const toggleSection = useCallback((sectionKey) => {
    setCollapsedSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  }, []);

  const handleAbonnementChange = useCallback(
    (index) => {
      const nieuwAbonnement = abonnementen[index];
      if (!nieuwAbonnement) return;
      const { beschikbareArtikelen: available, selectedArtikel, usePostzegel: shouldUsePostzegel } =
        computeBeschikbareArtikelen(nieuwAbonnement, artikelen || [], formState.interfaceArtikel);

      setFormState({ abonnement: nieuwAbonnement, interfaceArtikel: selectedArtikel || available[0] });
      setBeschikbareArtikelen(available);
      setUsePostzegel(shouldUsePostzegel);
      setFormState((prev) => ({
        postzegel: shouldUsePostzegel ? prev.postzegel ?? null : null
      }));
      setResponseMessage("");
    },
    [abonnementen, artikelen, formState.interfaceArtikel, postzegels]
  );

  const handleInterfaceArtikelChange = useCallback(
    (option) => {
      if (!option || !beschikbareArtikelen) return;
      setFormState({ interfaceArtikel: beschikbareArtikelen[option.value] });
    },
    [beschikbareArtikelen]
  );

  const handleRelatieChange = useCallback(
    (option, type) => {
      if (!relaties) return;
      if (!option) {
        if (type === "verzender") {
          setFormState({ mkgVerzender: null });
        } else {
          setFormState({ mkgOntvanger: null });
        }
        return;
      }

      const relatie = relaties[option.value];
      if (type === "verzender") {
        setFormState({ mkgVerzender: relatie });
      } else {
        setFormState({ mkgOntvanger: relatie });
      }
    },
    [relaties]
  );

  const handleGetAuth = useCallback(() => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 12; i += 1) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    const base64 = formState.mkgOntvanger
      ? `Basic ${btoa(`${formState.mkgOntvanger.rela_num}:${result}`)}`
      : NO_RECEIVER_AUTH;

    setFormState({ wachtwoord: result, auth: base64 });
  }, [formState.mkgOntvanger]);

  const handleChangeStatus = useCallback(
    (value) => {
      if (value && value !== formState.status) {
        setFormState({ status: value, statusOverschreven: true });
      }
    },
    [formState.status]
  );

  const handleAddMailEntry = useCallback(() => {
    setMailEntries((prev) => [...prev, ""]);
  }, []);

  const handleUpdateMailEntry = useCallback((index, value) => {
    setMailEntries((prev) => prev.map((entry, i) => (i === index ? value : entry)));
  }, []);

  const handleRemoveMailEntry = useCallback((index) => {
    setMailEntries((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.length > 0 ? updated : [""];
    });
  }, []);

  const handleSubmit = useCallback(async (shouldRedirect = true) => {
    if (filterManagerRef.current) {
      filterManagerRef.current.SaveFilters();
    }

    if (!updateMode && formState.cp_interface == null) {
      setFlashMessage(
        t('interface.requiredCpInterface')
      );
      return;
    }
    if (!formState.abonnement) {
      setFlashMessage(t('interface.requiredSubscription'));
      return;
    }
    if (!formState.interfaceArtikel) {
      setFlashMessage(t('interface.requiredInterfaceArticle'));
      return;
    }
    if (usePostzegel && !formState.postzegel) {
      setFlashMessage(t('interface.requiredPostzegel'));
      return;
    }
    if (!formState.mkgVerzender) {
      setFlashMessage(t('interface.requiredSenderRel'));
      return;
    }
    if (!formState.mkgOntvanger) {
      setFlashMessage(t('interface.requiredReceiverRel'));
      return;
    }
    if (formState.aantal < 1) {
      setFlashMessage(t('interface.invalidQuantity'));
      return;
    }
    if (formState.auth === NO_RECEIVER_AUTH) {
      setFlashMessage(t('interface.selectReceiverRefreshAuth'));
      return;
    }
    if (
      formState.abonnement.einddatum != null &&
      formState.einddatum != null &&
      new Date(formState.einddatum).getTime() >
        new Date(formState.abonnement.einddatum.split("T")[0]).getTime()
    ) {
      setFlashMessage(
        t('interface.endDateAfterSubscriptionEnd', {
          date: formState.abonnement.einddatum.toString().split("T")[0]
        })
      );
      return;
    }
    if (
      formState.abonnement.startdatum != null &&
      formState.startdatum != null &&
      new Date((formState.startdatum || "").split("T")[0]).getTime() <
        new Date(formState.abonnement.startdatum.split("T")[0]).getTime()
    ) {
      setFlashMessage(
        t('interface.startDateBeforeSubscriptionStart', {
          date: formState.abonnement.startdatum.toString().split("T")[0]
        })
      );
      return;
    }

    let status = "Inactief";
    if (!formState.interface) {
      const startdatum = formState.startdatum;
      const einddatum = formState.einddatum;
      const today = Date.now();
      if (startdatum && new Date(startdatum).getTime() <= today) {
        status = "Actief";
      }
      if (einddatum && startdatum && new Date(einddatum).getTime() <= new Date(startdatum).getTime()) {
        setFlashMessage(t('interface.endDateBeforeStart'));
        return;
      }
    } else {
      status = formState.status;
    }

    const postzegelDto = usePostzegel && formState.postzegel
      ? {
          id: formState.postzegel.id,
          code: formState.postzegel.code,
          aantalGratisBerichten: 0
        }
      : null;

    const interfaceDTO = {
      abonnementId: formState.abonnement.abonnementId,
      artikelNr: formState.interfaceArtikel.artikelNr,
      postzegel: postzegelDto,
      startdatum: formState.startdatum,
      einddatum: formState.einddatum,
      aanmaakdatum: formState.aanmaakdatum,
      status,
      status_overschreven: formState.statusOverschreven,
      mkg_relaNr_verzend: formState.mkgVerzender.rela_num,
      mkg_relaNr_ontvangt: formState.mkgOntvanger.rela_num,
      wachtwoord: formState.wachtwoord,
      auth: formState.auth,
      sftp_host: formState.sftpHost,
      sftp_poort: formState.sftpPort,
      sftp_map: formState.sftpMap,
      sftp_gebruiker: formState.sftpUsername,
      sftp_wachtwoord: formState.sftpPassword,
      gebruiktNokavision: formState.gebruiktNokavision,
      extra01: formState.PDFMap,
      extra02: formState.connectionType,
      extra03: formState.GLNCode,
      extra04: formState.Note,
      extra05: formState.Creadentials,
      mailadres: toCsvValues(mailEntries),
      listener: formState.DynamicListener,
      refreshToken: formState.refreshToken,
      accessToken: formState.accessToken,
      factuur_referentie: formState.factuur_referentie,
      cp_interface: formState.cp_interface,
      aantal: 1
    };

    try {
      if (updateMode) {
        const url = `${process.env.REACT_APP_API_URL}/Interfaces/${formState.interfaceId}`;
        const response = await axiosClientRef.current.put(url, interfaceDTO);
        if (response.status === 200) {
          setFlashMessage(t('interface.updateSuccess'), true);
          if (shouldRedirect) {
            setRedirect(true);
          }
        }
      } else {
        const url = `${process.env.REACT_APP_API_URL}/Interfaces`;
        const response = await axiosClientRef.current.post(url, interfaceDTO);
        if (response.status === 200) {
          setFlashMessage(t('interface.addSuccess'), true);
          if (!shouldRedirect) {
            let createdInterface = null;
            let createdInterfaceId = null;

            if (response.data && typeof response.data === "object") {
              createdInterface = response.data;
              createdInterfaceId = response.data.interfaceId ?? response.data.id ?? null;
            } else if (response.data !== undefined && response.data !== null) {
              const parsed = Number(response.data);
              createdInterfaceId = Number.isFinite(parsed) ? parsed : null;
            }

            if (createdInterfaceId == null) {
              createdInterfaceId = formState.interfaceId || null;
            }

            if (createdInterfaceId != null) {
              const createdInterfaceState = createdInterface || {
                ...interfaceDTO,
                interfaceId: createdInterfaceId,
              };

              setFormState({ interface: createdInterfaceState, interfaceId: createdInterfaceId });
              setSelectedInterface(createdInterfaceState);
              valuesAppliedRef.current = true;
              setUpdateMode(true);
            }
          }
          if (shouldRedirect) {
            setRedirect(true);
          }
        }
      }
    } catch (err) {
      setFlashMessage(err.toString());
    }
  }, [axiosClientRef, formState, mailEntries, updateMode, usePostzegel, setFlashMessage, t]);

  if (redirect && formState.klant) {
    return <Redirect to={`/klant/${formState.klant.klantId}`} />;
  }

  if (error) {
    return (
      <div className="error-alert">
        <h3>{t('common.error')}</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (loading || !formState.klant || !artikelen || !postzegels || !relaties || !abonnementen.length) {
    return (
      <div className="loading-container">
        <div className="loading-text">{t('common.loading')}</div>
      </div>
    );
  }

  if (!formState.klant) {
    return <div>{t('interface.noCustomerSelected')}</div>;
  }

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <h2>{updateMode ? t('interface.updateTitle') : t('interface.addTitle')}</h2>
      </div>
      <div className="page-content">
        <div className="space-y-4">
          <div className="parameter-section">
            <div 
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
              onClick={() => toggleSection("interface")}
            >
                <h3>{updateMode ? t('interface.updateTitle') : t('interface.addTitle')}</h3>
              <button className="btn btn-secondary" onClick={(e) => { e.stopPropagation(); toggleSection("interface"); }}>
                {collapsedSections.interface ? ">" : "v"}
              </button>
            </div>
        {!collapsedSections.interface && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {updateMode && (
            <>
              <div className="parameter-group">
                <label className="parameter-label">{t('interface.labelId')}</label>
                <input className="parameter-input" style={{ backgroundColor: '#e5e7eb' }} value={formState.interfaceId || ""} readOnly />
              </div>
              <div className="parameter-group">
                <label className="parameter-label">{t('interface.labelCreatedDate')}</label>
                <input
                  className="parameter-input"
                  style={{ backgroundColor: '#e5e7eb' }}
                  value={formState.aanmaakdatum ? formState.aanmaakdatum.toString().split("T")[0] : ""}
                  readOnly
                />
              </div>
            </>
          )}

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelSubscription')}</label>
            <select
              name="selectAbonnement"
              className="parameter-select"
              onChange={(evt) => handleAbonnementChange(Number(evt.target.value))}
              value={formState.abonnement ? abonnementen.indexOf(formState.abonnement) : 0}
            >
              {abonnementen.map((abo, i) => (
                <option value={i} key={abo.abonnementId || i}>
                  {abo.abonnementType.naam.toString()}
                </option>
              ))}
            </select>
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelArticleNumber')}</label>
            <Select
              name="selectArtikel"
              className="parameter-select"
              classNamePrefix="react-select"
              onChange={handleInterfaceArtikelChange}
              value={selectedInterfaceArtikelOption}
              options={interfaceArtikelOptions}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelConnectPlaza')}</label>
            <input
              name="cp_interface"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ cp_interface: evt.target.value })}
              value={formState.cp_interface || ""}
            />
          </div>

          {usePostzegel && (
            <div className="parameter-group">
              <label className="parameter-label">{t('interface.labelStamps')}</label>
              <select
                name="selectPostzegel"
                className="parameter-select"
                onChange={(evt) => setFormState({ postzegel: postzegels[Number(evt.target.value)] })}
                value={postzegelIndex}
              >
                <option value="" disabled>
                  {`-- ${t('common.select')} --`}
                </option>
                {postzegels.map((post, i) => (
                  <option value={i} key={post.id || i}>
                    {post.code.toString()}
                  </option>
                ))}
              </select>
            </div>
          )}

          <RenderOnRole roles={["Administrator"]}>
            <div className="parameter-group">
              <label className="parameter-label">{t('subscriptions.startDate')}</label>
              <input
                name="selectStartdate"
                type="date"
                className="parameter-input"
                onChange={(evt) => setFormState({ startdatum: evt.target.value })}
                value={formState.startdatum ? formState.startdatum.toString().split("T")[0] : ""}
              />
            </div>

            <div className="parameter-group">
              <label className="parameter-label">{t('interface.labelStatus')}</label>
              <select
                name="selectStatus"
                className="parameter-select"
                onChange={(evt) => handleChangeStatus(evt.target.value)}
                value={formState.status || ""}
              >
                {!formState.status ? <option value="" /> : null}
                <option value="Actief">{t('interface.statusActive')}</option>
                <option value="Inactief">{t('interface.statusInactive')}</option>
              </select>
            </div>
          </RenderOnRole>

          <div className="parameter-group">
            <label className="parameter-label">{t('subscriptions.endDate')}</label>
            <input
              name="selectEnddate"
              type="date"
              className="parameter-input"
              onChange={(evt) => setFormState({ einddatum: evt.target.value })}
              value={formState.einddatum ? formState.einddatum.toString().split("T")[0] : ""}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelSenderRel')}</label>
            <Select
              name="mkgVerzender"
              className="parameter-select"
              classNamePrefix="react-select"
              onChange={(option) => handleRelatieChange(option, "verzender")}
              value={selectedVerzenderOption}
              options={relatieOptions}
              placeholder={`-- ${t('common.select')} --`}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelSenderUniqueId')}</label>
            <input
              className="parameter-input"
              style={{ backgroundColor: '#e5e7eb' }}
              type="text"
              readOnly
              value={formState.mkgVerzender ? formState.mkgVerzender.uniekid : ""}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelReceiverRel')}</label>
            <Select
              name="mkgOntvanger"
              className="parameter-select"
              classNamePrefix="react-select"
              onChange={(option) => handleRelatieChange(option, "ontvanger")}
              value={selectedOntvangerOption}
              options={relatieOptions}
              placeholder={`-- ${t('common.select')} --`}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelReceiverUniqueId')}</label>
            <input
              className="parameter-input"
              style={{ backgroundColor: '#e5e7eb' }}
              type="text"
              readOnly
              value={formState.mkgOntvanger ? formState.mkgOntvanger.uniekid : ""}
            />
          </div>
        </div>
        )}
      </div>

      <div className="parameter-section">
        <div 
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
          onClick={() => toggleSection("auth")}
        >
          <h3>{t('interface.sectionAuth')}</h3>
          <button className="btn btn-secondary" onClick={(e) => { e.stopPropagation(); toggleSection("auth"); }}>
            {collapsedSections.auth ? ">" : "v"}
          </button>
        </div>
        {!collapsedSections.auth && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelAuthentication')}</label>
            <div className="action-buttons">
              <button className="btn btn-secondary" onClick={handleGetAuth}>
                {t('common.reset')}
              </button>
              <input
                name="wachtwoord"
                className="parameter-input"
                type="text"
                onChange={(evt) => setFormState({ wachtwoord: evt.target.value })}
                value={formState.wachtwoord || ""}
              />
            </div>
            <input
              name="auth"
              className="parameter-input"
              style={{ backgroundColor: '#e5e7eb' }}
              type="text"
              value={formState.auth === NO_RECEIVER_AUTH ? t('interface.selectReceiverFirst') : (formState.auth || "")}
              readOnly
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelRefreshToken')}</label>
            <input
              name="refreshtoken"
              className="parameter-input"
              type="text"
              value={formState.refreshToken || ""}
              onChange={(evt) => setFormState({ refreshToken: evt.target.value })}
            />
          </div>
        </div>
        )}
      </div>

      <div className="parameter-section">
                <div 
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
          onClick={() => toggleSection("sftp")}
        >
          <h3>{t('interface.sectionSftp')}</h3>
          <button className="btn btn-secondary" onClick={(e) => { e.stopPropagation(); toggleSection("sftp"); }}>
            {collapsedSections.sftp ? ">" : "v"}
          </button>
        </div>
        {!collapsedSections.sftp && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelHost')}</label>
            <input
              name="sftphost"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ sftpHost: evt.target.value })}
              value={formState.sftpHost || ""}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelPort')}</label>
            <input
              name="sftppoort"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ sftpPort: evt.target.value })}
              value={formState.sftpPort || ""}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelMap')}</label>
            <input
              name="sftpmap"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ sftpMap: evt.target.value })}
              value={formState.sftpMap || ""}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelUsername')}</label>
            <input
              name="sftpuser"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ sftpUsername: evt.target.value })}
              value={formState.sftpUsername || ""}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelPassword')}</label>
            <input
              name="sftppassword"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ sftpPassword: evt.target.value })}
              value={formState.sftpPassword || ""}
            />
          </div>
        </div>
        )}
      </div>

      <div className="parameter-section">
        <div 
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
          onClick={() => toggleSection("overig")}
        >
          <h3>{t('interface.sectionOther')}</h3>
          <button className="btn btn-secondary" onClick={(e) => { e.stopPropagation(); toggleSection("overig"); }}>
            {collapsedSections.overig ? ">" : "v"}
          </button>
        </div>
        {!collapsedSections.overig && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelUseNokavision')}</label>
            <input
              name="cbGebruiktNokavision"
              type="checkbox"
              className="parameter-input"
              checked={formState.gebruiktNokavision}
              onChange={(evt) => setFormState({ gebruiktNokavision: evt.target.checked })}
            />
          </div> */}

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelPdfMap')}</label>
            <input
              name="pdfMap"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ PDFMap: evt.target.value })}
              value={formState.PDFMap || ""}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelConnectionType')}</label>
            <input
              name="connectieType"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ connectionType: evt.target.value })}
              value={formState.connectionType || ""}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelGlnCode')}</label>
            <input
              name="glnCode"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ GLNCode: evt.target.value })}
              value={formState.GLNCode || ""}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelNotes')}</label>
            <input
              name="opmerkingen"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ Note: evt.target.value })}
              value={formState.Note || ""}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelCredentials')}</label>
            <input
              name="credentials"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ Creadentials: evt.target.value })}
              value={formState.Creadentials || ""}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelEmail')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {mailEntries.map((mail, index) => (
                <div
                  key={`mail-${index}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}
                >
                  <input
                    name={`email-${index}`}
                    className="parameter-input"
                    type="text"
                    placeholder="name@example.com"
                    onChange={(evt) => handleUpdateMailEntry(index, evt.target.value)}
                    value={mail}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => handleRemoveMailEntry(index)}
                    aria-label={`Remove email ${index + 1}`}
                  >
                    x
                  </button>
                </div>
              ))}
              <button className="btn btn-secondary" type="button" onClick={handleAddMailEntry}>
                {t('interface.addEmail')}
              </button>
            </div>
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelDynamicListener')}</label>
            <input
              name="dynamische listener"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ DynamicListener: evt.target.value })}
              value={formState.DynamicListener || ""}
            />
          </div>

          <div className="parameter-group">
            <label className="parameter-label">{t('interface.labelInvoiceReference')}</label>
            <input
              name="factuur_referentie"
              className="parameter-input"
              type="text"
              onChange={(evt) => setFormState({ factuur_referentie: evt.target.value })}
              value={formState.factuur_referentie || ""}
            />
          </div>
        </div>
        )}
      </div>

      {updateMode && (
        <div className="parameter-section">
          <div 
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
            onClick={() => toggleSection("melding")}
          >
            <h3>{t('interface.sectionExceptions')}</h3>
            <button className="btn btn-secondary" onClick={(e) => { e.stopPropagation(); toggleSection("melding"); }}>
              {collapsedSections.melding ? ">" : "v"}
            </button>
          </div>
          {!collapsedSections.melding && (
            <>
              <p className="parameter-label" style={{ marginTop: '0.5rem' }}>
                {t('interface.exceptionsDescriptionLine1')}<br />
                {t('interface.exceptionsDescriptionLine2')}<br />
                {t('interface.exceptionsDescriptionLine3')}
              </p>
              <FilterManager ref={filterManagerRef} interfaceId={formState.interfaceId} />
            </>
          )}
        </div>
      )}

      {updateMode && (
        <div className="parameter-section">
          <div
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
            onClick={() => toggleSection("flagged")}
          >
            <h3>{t('interface.sectionFlaggedState')}</h3>
            <button className="btn btn-secondary" onClick={(e) => { e.stopPropagation(); toggleSection("flagged"); }}>
              {collapsedSections.flagged ? ">" : "v"}
            </button>
          </div>
          {!collapsedSections.flagged && (
            <FlaggedState interfaceId={formState.interfaceId} />
          )}
        </div>
      )}

      <div className="action-bar">
        <div className="action-buttons">
          <button className="btn btn-primary" onClick={() => handleSubmit(false)}>
            {updateMode ? t('interface.updateAction') : t('interface.addAction')}
          </button>
          <button className="btn btn-success" onClick={() => handleSubmit(true)}>
            {updateMode ? t('interface.updateAndCloseAction') : t('interface.addAndCloseAction')}
          </button>
          {responseMessage && (
            <div 
              key={messageKey}
              className="font-semibold" 
              style={{ 
                color: responseIsSuccess ? "#16a34a" : "#dc2626",
                animation: "flash 0.5s ease-in-out"
              }}
            >
              {responseMessage}
            </div>
          )}
          <style>{`
            @keyframes flash {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.3; transform: scale(1.05); }
            }
          `}</style>
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

 export default InterfaceToevoegenPagina;
 